/**
 * Core File Storage Service
 * Centralized file management for all uploads/downloads/preview
 * Used by Documents, Photos, Attachments, etc.
 */

import { httpClient, ApiResponse } from './http.client';
import { authService } from './auth.service';
import { servicesConfig } from '../config/services.config';

export type FileCategory =
  | 'document'
  | 'photo'
  | 'attachment'
  | 'drawing'
  | 'report';

export interface FileStorage {
  storage_id: string;
  file_name: string;
  file_size_bytes: number;
  file_size_mb: number;
  mime_type: string;
  file_category: FileCategory;
  organization_id: string;
  uploaded_by: string;
  uploaded_at: string;
  storage_path: string;
  checksum?: string;
}

export interface FileUploadResponse {
  storage_id: string;
  file_name: string;
  file_size_bytes: number;
  file_size_mb: number;
  mime_type: string;
  storage_path: string;
  uploaded_at: string;
}

export interface DownloadUrlResponse {
  download_url: string;
  expires_at: string;
}

class FileStorageService {
  /**
   * STEP 1: Upload file to storage
   * This is the core upload function used by all modules
   * @param file - File to upload
   * @param fileCategory - Category of file (document, photo, attachment, etc.)
   * @param organizationId - Optional organization ID
   * @returns Storage ID and file metadata
   */
  async uploadFile(
    file: File,
    fileCategory: FileCategory,
    organizationId?: string
  ): Promise<ApiResponse<FileUploadResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_category', fileCategory);

    // Add organization_id as query param if provided
    const queryParams = new URLSearchParams();
    if (organizationId) {
      queryParams.append('organization_id', organizationId);
    }

    const url = `/api/v1/file-storage/upload${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    // Don't set Content-Type for FormData - browser will set it with correct boundary
    return httpClient.post<FileUploadResponse>(url, formData);
  }

  /**
   * Get download URL with presigned link
   * @param storageId - Storage ID from file upload
   * @param expiration - URL expiration in seconds (default: 3600 = 1 hour)
   */
  async getDownloadUrl(
    storageId: string,
    expiration: number = 3600
  ): Promise<ApiResponse<DownloadUrlResponse>> {
    const queryParams = new URLSearchParams({
      expiration: expiration.toString()
    });

    return httpClient.get<DownloadUrlResponse>(
      `/api/v1/file-storage/${storageId}/download-url?${queryParams}`
    );
  }

  /**
   * Download file directly (opens blob)
   * @param storageId - Storage ID
   */
  async downloadFile(storageId: string): Promise<ApiResponse<Blob>> {
    const token = authService.getAccessToken();
    const baseURL = servicesConfig.backend.baseUrl;

    const response = await fetch(`${baseURL}/api/v1/file-storage/${storageId}/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    return {
      success: true,
      data: blob,
      message: 'Download successful',
      status: response.status
    };
  }

  /**
   * Preview file (for images, PDFs, etc.)
   * Returns presigned URL with short expiration
   */
  async getPreviewUrl(storageId: string): Promise<ApiResponse<DownloadUrlResponse>> {
    return this.getDownloadUrl(storageId, 300); // 5 minutes expiration
  }

  /**
   * Preview file directly (opens blob in new tab)
   */
  async previewFile(storageId: string): Promise<ApiResponse<Blob>> {
    return this.downloadFile(storageId);
  }

  /**
   * Delete file from storage
   * @param storageId - Storage ID to delete
   */
  async deleteFile(storageId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/file-storage/${storageId}`);
  }

  /**
   * Get file storage details
   * @param storageId - Storage ID
   */
  async getFileStorage(storageId: string): Promise<ApiResponse<FileStorage>> {
    return httpClient.get<FileStorage>(`/api/v1/file-storage/${storageId}`);
  }

  /**
   * Bulk upload files
   * @param files - Array of files to upload
   * @param fileCategory - Category for all files
   * @param organizationId - Optional organization ID
   */
  async bulkUploadFiles(
    files: File[],
    fileCategory: FileCategory,
    organizationId?: string
  ): Promise<ApiResponse<{
    successful: FileUploadResponse[];
    failed: Array<{ filename: string; error: string }>;
  }>> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('file_category', fileCategory);

    const queryParams = new URLSearchParams();
    if (organizationId) {
      queryParams.append('organization_id', organizationId);
    }

    const url = `/api/v1/file-storage/bulk-upload${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    return httpClient.post<{
      successful: FileUploadResponse[];
      failed: Array<{ filename: string; error: string }>;
    }>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Utility: Download file and trigger browser download
   * @param storageId - Storage ID
   * @param fileName - Optional custom filename for download
   */
  async triggerDownload(storageId: string, fileName?: string): Promise<void> {
    const response = await this.downloadFile(storageId);
    if (response.success && response.data) {
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Utility: Open file in new tab
   * @param storageId - Storage ID
   */
  async openInNewTab(storageId: string): Promise<void> {
    const response = await this.previewFile(storageId);
    if (response.success && response.data) {
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank');
    }
  }
}

// Export singleton instance
export const fileStorageService = new FileStorageService();
